<template>
  <div class="logists-tab">
    <div class="mode-row">
      <div class="limit-chips">
        <button
          v-for="opt in LIMITS"
          :key="String(opt.id)"
          class="limit-btn"
          type="button"
          :class="{ active: rowLimit === opt.id }"
          @click="rowLimit = opt.id"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>
    <div class="chart-layout">
      <div class="chart-settings">
        <div class="settings-title">Настройки графика</div>
        <div class="settings-section" v-for="group in metricGroups" :key="group.label">
          <div class="settings-label">{{ group.label }}</div>
          <div class="metrics-list">
            <button
              v-for="metric in group.metrics"
              :key="metric.field"
              type="button"
              class="metric-chip"
              :class="{ selected: isSelected(metric) }"
              :title="isSelected(metric) ? 'Убрать метрику (Shift+клик — только эта)' : metric.label"
              @click="toggleMetric(metric, $event)"
            >
              <span class="metric-dot" :style="{ background: metric.color }" />
              {{ metric.label }}
            </button>
          </div>
        </div>
        <div class="settings-section">
          <div v-if="selectedFields.length > 1" class="settings-hint">Несколько метрик — сгруппированные бары</div>
          <div class="settings-hint">Shift+клик — оставить только эту метрику</div>
        </div>
      </div>
      <div class="chart-block">
        <LogistsCompareChart
          title="Логисты: сравнение метрик"
          :rows="displayRows"
          :metrics="selectedMetrics"
          :empty-text="isLoading ? 'Загрузка…' : 'Нет данных по выбранным фильтрам'"
        />
      </div>
    </div>
    <div class="table-scroll">
      <table class="stat-table" v-if="rows.length">
        <thead>
          <tr>
            <th class="sortable" :class="{ active: sortKey === 'logist' }" @click="setSort('logist')">
              Логист <span v-if="sortKey === 'logist'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'trips' }" @click="setSort('trips')">
              Рейсов <span v-if="sortKey === 'trips'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'closed' }" @click="setSort('closed')">
              Закрыто <span v-if="sortKey === 'closed'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_margin_pct' }" @click="setSort('avg_margin_pct')">
              Ср. маржа <span v-if="sortKey === 'avg_margin_pct'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_profitability_pct' }" @click="setSort('avg_profitability_pct')">
              Ср. рентаб. <span v-if="sortKey === 'avg_profitability_pct'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_base_deviation_pct' }" @click="setSort('avg_base_deviation_pct')" title="Среднее отклонение расчётной цены перевозчика от базовой">
              Откл. от базы <span v-if="sortKey === 'avg_base_deviation_pct'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_customer_gross' }" @click="setSort('avg_customer_gross')" title="Средняя ставка заказчика (с НДС)">
              Ср. ставка <span v-if="sortKey === 'avg_customer_gross'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'total_commission' }" @click="setSort('total_commission')">
              Комиссия <span v-if="sortKey === 'total_commission'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'net_commission_rub' }" @click="setSort('net_commission_rub')">
              Чистая комиссия <span v-if="sortKey === 'net_commission_rub'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_commission' }" @click="setSort('avg_commission')">
              Ср. комиссия <span v-if="sortKey === 'avg_commission'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'breakdown_count' }" @click="setSort('breakdown_count')">
              Срывы <span v-if="sortKey === 'breakdown_count'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'breakdown_loss_rub' }" @click="setSort('breakdown_loss_rub')">
              Штрафы срывов <span v-if="sortKey === 'breakdown_loss_rub'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_search_sec' }" @click="setSort('avg_search_sec')">
              Ср. поиск, ч. <span v-if="sortKey === 'avg_search_sec'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'fallback_search' }" @click="setSort('fallback_search')">
              Fallback <span v-if="sortKey === 'fallback_search'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'expired_total' }" @click="setSort('expired_total')">
              Просрочки <span v-if="sortKey === 'expired_total'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'closed_early' }" @click="setSort('closed_early')" title="Закрыто ≥24 ч до плановой погрузки">
              ≥24 ч <span v-if="sortKey === 'closed_early'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'closed_lastday' }" @click="setSort('closed_lastday')" title="Закрыто за 12–24 ч до плановой погрузки">
              12–24 ч <span v-if="sortKey === 'closed_lastday'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'closed_late' }" @click="setSort('closed_late')" title="Закрыто <12 ч до погрузки или после неё">
              &lt;12 ч <span v-if="sortKey === 'closed_late'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_hours_to_load' }" @click="setSort('avg_hours_to_load')" title="Среднее время от закрытия до погрузки, ч">
              До погрузки, ч <span v-if="sortKey === 'avg_hours_to_load'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortedRows" :key="row.logist">
            <td class="name-cell">{{ row.logist || '—' }}</td>
            <td class="num">{{ fmtNum(row.trips) }}</td>
            <td class="num">{{ fmtNum(row.closed) }}</td>
            <td class="num">{{ fmtPct(row.avg_margin_pct) }}</td>
            <td class="num">{{ fmtPct(row.avg_profitability_pct) }}</td>
            <td class="num">{{ fmtPct(row.avg_base_deviation_pct) }}</td>
            <td class="num">{{ fmtMoney(row.avg_customer_gross) }}</td>
            <td class="num strong">{{ fmtMoney(row.total_commission) }}</td>
            <td class="num">{{ fmtMoney(row.net_commission_rub) }}</td>
            <td class="num">{{ fmtMoney(row.avg_commission) }}</td>
            <td class="num" :class="{ 'warn-cell': row.breakdown_count > 0 }">{{ fmtNum(row.breakdown_count) }}</td>
            <td class="num" :class="{ 'warn-cell': row.breakdown_loss_rub > 0 }">{{ fmtMoney(row.breakdown_loss_rub) }}</td>
            <td class="num">{{ fmtHoursNum(row.avg_search_sec) }}</td>
            <td class="num" :class="{ 'warn-cell': row.fallback_search > 0 }">{{ fmtNum(row.fallback_search) }}</td>
            <td class="num" :class="{ 'warn-cell': row.expired_total > 0 }">{{ fmtNum(row.expired_total) }}</td>
            <td class="num good-cell">{{ fmtNum(row.closed_early) }}</td>
            <td class="num">{{ fmtNum(row.closed_lastday) }}</td>
            <td class="num" :class="{ 'warn-cell': row.closed_late > 0 }">{{ fmtNum(row.closed_late) }}</td>
            <td class="num">{{ fmtNum(row.avg_hours_to_load, 1) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!isLoading" class="empty-table">Нет данных по выбранным фильтрам</div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import LogistsCompareChart from './LogistsCompareChart.vue';
import { fmtHoursNum, fmtMoney, fmtNum, fmtPct } from '../format';

const METRICS = [
  { field: 'trips', label: 'Рейсов', formatter: fmtNum, color: '#3b82f6' },
  { field: 'closed', label: 'Закрыто', formatter: fmtNum, color: '#12b76a' },
  { field: 'avg_margin_pct', label: 'Ср. маржа', formatter: fmtPct, color: '#12b76a' },
  { field: 'avg_profitability_pct', label: 'Ср. рентаб.', formatter: fmtPct, color: '#f79009' },
  { field: 'total_commission', label: 'Комиссия', formatter: fmtMoney, color: '#3b82f6' },
  { field: 'net_commission_rub', label: 'Чистая комиссия', formatter: fmtMoney, color: '#12b76a' },
  { field: 'avg_commission', label: 'Ср. комиссия', formatter: fmtMoney, color: '#667085' },
  { field: 'breakdown_loss_rub', label: 'Штрафы за срывы', formatter: fmtMoney, color: '#f04438' },
  { field: 'breakdown_count', label: 'Срывы', formatter: fmtNum, color: '#f04438' },
  { field: 'avg_search_sec', label: 'Ср. поиск, ч.', formatter: fmtHoursNum, color: '#7a5af8' },
  { field: 'expired_total', label: 'Просрочки', formatter: fmtNum, color: '#f04438' },
  { field: 'closed_early', label: 'Закрыто ≥24 ч', formatter: fmtNum, color: '#12b76a' },
  { field: 'closed_lastday', label: 'Закрыто 12–24 ч', formatter: fmtNum, color: '#f79009' },
  { field: 'closed_late', label: 'Закрыто <12 ч', formatter: fmtNum, color: '#f04438' },
  { field: 'avg_hours_to_load', label: 'До погрузки, ч', formatter: (v) => fmtNum(v, 1), color: '#667085' },
  { field: 'avg_base_deviation_pct', label: 'Откл. от базы', formatter: fmtPct, color: '#667085' },
  { field: 'avg_customer_gross', label: 'Ср. ставка', formatter: fmtMoney, color: '#3b82f6' },
];

// Группировка метрик по смыслу для настроек графика
const METRIC_GROUPS = [
  { label: 'Объёмы', fields: ['trips', 'closed'] },
  { label: 'Деньги', fields: ['total_commission', 'net_commission_rub', 'avg_commission', 'breakdown_loss_rub'] },
  { label: 'Маржинальность и цены', fields: ['avg_margin_pct', 'avg_profitability_pct', 'avg_base_deviation_pct', 'avg_customer_gross'] },
  { label: 'Сроки', fields: ['avg_search_sec', 'avg_hours_to_load'] },
  { label: 'Надёжность', fields: ['breakdown_count', 'expired_total', 'closed_early', 'closed_lastday', 'closed_late'] },
];

const LIMITS = [
  { id: 'all', label: 'Все' },
  { id: 5, label: '5' },
  { id: 10, label: '10' },
  { id: 20, label: '20' },
  { id: 50, label: '50' },
];

// Сохранённый вид вкладки: метрики графика + сортировка таблицы + лимит строк
const VIEW_STORAGE_KEY = 'statisticsLogistsView';

function loadView() {
  const fallback = { fields: ['total_commission'], sortKey: 'total_commission', sortDir: 'desc', rowLimit: 'all' };
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const known = new Set(METRICS.map((m) => m.field));
    const fields = Array.isArray(parsed?.fields) ? parsed.fields.filter((f) => typeof f === 'string' && known.has(f)) : [];
    if (!fields.length) fields.push('total_commission');
    const rowLimit = LIMITS.some((l) => l.id === parsed?.rowLimit) ? parsed.rowLimit : 'all';
    return {
      fields,
      sortKey: typeof parsed?.sortKey === 'string' ? parsed.sortKey : fallback.sortKey,
      sortDir: parsed?.sortDir === 'asc' ? 'asc' : 'desc',
      rowLimit,
    };
  } catch (e) {
    return fallback;
  }
}

export default {
  name: 'LogistsTab',
  components: { LogistsCompareChart },
  props: {
    rows: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  },
  setup(props) {
    const initial = loadView();
    const selectedFields = ref(initial.fields);
    const sortKey = ref(initial.sortKey);
    const sortDir = ref(initial.sortDir);
    const rowLimit = ref(initial.rowLimit);

    watch(
      [selectedFields, sortKey, sortDir, rowLimit],
      () => {
        try {
          window.localStorage.setItem(
            VIEW_STORAGE_KEY,
            JSON.stringify({
              fields: selectedFields.value,
              sortKey: sortKey.value,
              sortDir: sortDir.value,
              rowLimit: rowLimit.value,
            })
          );
        } catch (e) {
          /* localStorage может быть недоступен — молча игнорируем */
        }
      },
      { deep: true }
    );

    const setSort = (key) => {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc';
        return;
      }
      sortKey.value = key;
      sortDir.value = key === 'logist' ? 'asc' : 'desc';
    };

    const sortedRows = computed(() => {
      const field = sortKey.value;
      const dir = sortDir.value === 'desc' ? -1 : 1;
      return [...props.rows].sort((a, b) => {
        if (field === 'logist') {
          const av = a.logist || '';
          const bv = b.logist || '';
          return av.localeCompare(bv, 'ru') * dir;
        }
        const av = Number(a[field]);
        const bv = Number(b[field]);
        const an = Number.isFinite(av) ? av : null;
        const bn = Number.isFinite(bv) ? bv : null;
        if (an === null && bn === null) return 0;
        if (an === null) return 1;
        if (bn === null) return -1;
        return (an - bn) * dir;
      });
    });

    const isSelected = (metric) => selectedFields.value.includes(metric.field);

    const toggleMetric = (metric, event) => {
      if (event && event.shiftKey) {
        selectedFields.value = [metric.field];
        return;
      }
      const fields = selectedFields.value;
      if (fields.includes(metric.field)) {
        if (fields.length === 1) return;
        selectedFields.value = fields.filter((f) => f !== metric.field);
        return;
      }
      const next = [...fields, metric.field];
      selectedFields.value = next;
    };

    const metricByField = (field) => METRICS.find((m) => m.field === field) || METRICS[4];
    const metricGroups = METRIC_GROUPS.map((g) => ({
      label: g.label,
      metrics: g.fields.map(metricByField).filter(Boolean),
    }));
    const metricA = computed(() => metricByField(selectedFields.value[0]));
    const selectedMetrics = computed(() =>
      selectedFields.value.map(metricByField).filter(Boolean)
    );

    const displayRows = computed(() => {
      const field = metricA.value.field;
      const sorted = [...props.rows].sort((a, b) => (Number(b[field]) || 0) - (Number(a[field]) || 0));
      return typeof rowLimit.value === 'number' ? sorted.slice(0, rowLimit.value) : sorted;
    });

    return {
      metrics: METRICS,
      metricGroups,
      LIMITS,
      rowLimit,
      selectedFields,
      isSelected,
      toggleMetric,
      metricA,
      selectedMetrics,
      displayRows,
      sortKey,
      sortDir,
      setSort,
      sortedRows,
      fmtHoursNum,
      fmtMoney,
      fmtNum,
      fmtPct,
    };
  },
};
</script>

<style scoped>
.logists-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.mode-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.limit-chips {
  display: flex;
  align-items: center;
  gap: 4px;
}

.limit-btn {
  padding: 5px 10px;
  font-size: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #475467;
  cursor: pointer;
}

.limit-btn:hover {
  background: #f8fafc;
}

.limit-btn.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #007bff;
  font-weight: 600;
}

.chart-layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .chart-layout {
    grid-template-columns: 1fr;
  }
}

.chart-settings {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-label {
  font-size: 12px;
  font-weight: 500;
  color: #667085;
}

.metrics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.metric-chip:hover {
  border-color: #98a2b3;
}

.metric-chip.selected {
  border-color: #007bff;
  background: #eff6ff;
}

.metric-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.slot-mark {
  font-size: 10px;
  font-weight: 700;
  color: #007bff;
}

.settings-hint {
  font-size: 11px;
  color: #98a2b3;
}

.chart-block {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  color: #667085;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.stat-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.stat-table th.sortable:hover {
  color: #344054;
}

.stat-table th.active {
  color: #007bff;
}

.sort-arrow {
  font-size: 9px;
}

.stat-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #1d2939;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.strong {
  font-weight: 600;
}

.name-cell {
  font-weight: 500;
}

.warn-cell {
  color: #d92d20;
}

.good-cell {
  color: #067647;
}

.empty-table {
  padding: 40px;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
